import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Table,
  Badge,
  Group,
  Button,
  Stack,
  Select,
  Pagination,
  ActionIcon,
  Tooltip,
  Modal,
  Alert,
  Anchor,
} from "@mantine/core";
import { IconPlus, IconEye, IconEdit, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useProjectSummarySheets, useDeleteProjectSummarySheet } from "../hooks/useProjectSummarySheet";
import type { ProjectSummarySheet } from "../../../api/projectSummarySheet";
import { dhivehiDisplayStyle } from "../../../utils/dhivehiText";
import { useAuth } from "../../../auth/AuthContext";
import { canEditProjectSummarySheet } from "../permissions";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  UNDER_PEER_REVIEW: "yellow",
  UNDER_HOP_REVIEW: "orange",
  APPROVED: "green",
  REJECTED: "red",
};

export function ProjectSummarySheetList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [row, setRow] = useState<ProjectSummarySheet | null>(null);

  const { data, isLoading, error } = useProjectSummarySheets({
    status: statusFilter || undefined,
    page,
    limit: 10,
  });
  const del = useDeleteProjectSummarySheet();

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
          Failed to load project summary sheets.
        </Alert>
      </Container>
    );
  }

  const sheets = data?.sheets ?? [];
  const totalPages = data?.pagination.pages ?? 1;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Project Summary Sheets (PR-03)</Title>
            <Text c="dimmed" size="sm">
              Prepare and approve summary sheets before adding them to bid committee agendas
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate("/project-summary-sheets/new")}>
            New sheet
          </Button>
        </Group>

        <Card shadow="sm" p="md">
          <Stack gap="md">
            <Select
              label="Status"
              clearable
              placeholder="All"
              data={[
                { value: "DRAFT", label: "Draft" },
                { value: "UNDER_PEER_REVIEW", label: "Peer review" },
                { value: "UNDER_HOP_REVIEW", label: "Head of Procurement" },
                { value: "APPROVED", label: "Approved" },
                { value: "REJECTED", label: "Rejected" },
              ]}
              value={statusFilter || null}
              onChange={(v) => {
                setStatusFilter(v || "");
                setPage(1);
              }}
            />
            {isLoading ? (
              <Text>Loading…</Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Project</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th w={120}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sheets.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        <Anchor component={Link} to={`/project-summary-sheets/${s.id}`} fw={500}>
                          {s.summarySheetNumber}
                        </Anchor>
                      </Table.Td>
                      <Table.Td style={dhivehiDisplayStyle(s.projectName)}>{s.projectName}</Table.Td>
                      <Table.Td>{new Date(s.sheetDate).toLocaleDateString()}</Table.Td>
                      <Table.Td>
                        <Badge color={statusColors[s.status] || "gray"} variant="light">
                          {s.status.replace(/_/g, " ")}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Tooltip label="View">
                            <ActionIcon variant="light" onClick={() => navigate(`/project-summary-sheets/${s.id}`)}>
                              <IconEye size={16} />
                            </ActionIcon>
                          </Tooltip>
                          {canEditProjectSummarySheet(s, user) && (
                            <Tooltip label="Edit">
                              <ActionIcon variant="light" onClick={() => navigate(`/project-summary-sheets/${s.id}/edit`)}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {(s.status === "DRAFT" || s.status === "REJECTED") && (
                            <Tooltip label="Delete">
                              <ActionIcon
                                color="red"
                                variant="light"
                                onClick={() => {
                                  setRow(s);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} mt="md" />
            )}
          </Stack>
        </Card>
      </Stack>

      <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete sheet?">
        <Stack>
          <Text size="sm">This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={del.isPending}
              onClick={() => {
                if (row) {
                  del.mutate(row.id, {
                    onSuccess: () => {
                      setDeleteModalOpen(false);
                      setRow(null);
                    },
                  });
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
